// @ts-nocheck
import { useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  Row,
  FilterFn,
  RowData,
} from "@tanstack/react-table";
import AddIcon from "@mui/icons-material/Add";
import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined";
import {
  Checkbox,
  Stack,
  Button,
  CircularProgress,
  Badge,
} from "@mui/material";

import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isEmpty, isEqual, sortBy, uniq } from "lodash";

import { parseISO, format } from "date-fns";

import cn from "classnames";
import Pagination from "@mui/material/Pagination";

import { MonitoringIssue, IssueStatus } from "../fixtures";

import ColumnFilter, {
  SimpleSort,
  ValuePickerFilter,
  DateThresholdFilter,
} from "./ColumnFilter";
import GlobalFilter from "./GlobalFilter";
import SelectColumn from "./SelectColumn";
import SelectColumnHeader from "./SelectColumn/ColumnHeader";
import RaggedTextSkeleton from "./RaggedTextSkeleton";
import ExportButton from "./ExportButton";
import AssignButton from "./AssignButton";

import {
  getAllIssues,
  getAllIssuesAllValues,
  getAllIssuesById,
  getOpenIssues,
  getOpenIssuesAllValues,
  getOpenIssuesById,
  getMutedIssues,
  getMutedIssuesAllValues,
  getMutedIssuesById,
  getResolvedIssues,
  getResolvedIssuesAllValues,
  getResolvedIssuesById,
} from "./queries";

import "./Table.css";

const columnHelper = createColumnHelper<MonitoringIssue>();

const columns = [
  new SelectColumn<MonitoringIssue>(),
  columnHelper.accessor("domain", {
    sortingFn: "alphanumeric",
    filterFn: "arrIncludesSome",
    meta: {
      filterVariant: "valuePicker",
    },
  }),
  columnHelper.accessor("status", {
    sortingFn: "alphanumeric",
    filterFn: "arrIncludesSome",
    meta: {
      filterVariant: "valuePicker",
    },
  }),
  columnHelper.accessor("date", {
    sortingFn: "datetime",
    cell: ({ cell }) => {
      const timestamp = cell.getValue();
      try {
        const parsed = parseISO(timestamp);
        return format(parsed, "MMM d, yyyy h:mm a");
      } catch (e) {
        return timestamp;
      }
    },
    filterFn: "dateRange",
    meta: {
      filterVariant: "dateThreshold",
    },
  }),
  columnHelper.accessor("alerts", {
    sortingFn: "alphanumeric",
    filterFn: "arrIncludesSome",
    meta: {
      filterVariant: "valuePicker",
    },
  }),
  columnHelper.accessor("assignee", {
    sortingFn: "alphanumeric",
    filterFn: "arrIncludesSome",
    meta: {
      filterVariant: "valuePicker",
      filterStickyValues: ["unassigned"],
    },
    cell: ({ cell, row }) => {
      const value = cell.getValue();
      const assignee = value?.email;

      const showBadge =
        !assignee && row.getValue("status") !== IssueStatus.Resolved;

      return (
        <Badge
          color="primary"
          variant="dot"
          badgeContent={showBadge ? 1 : 0}
          sx={{
            "& .MuiBadge-badge": {
              top: "50%",
              right: "-0.75em",
            },
          }}
        >
          {assignee || "unassigned"}
        </Badge>
      );
    },
  }),
];

const loadingColumns: ColumnDef<MonitoringIssue>[] = [
  {
    id: "select",
    header: () => <SelectColumnHeader disabled />,
    cell: () => <Checkbox disabled />,
  },
  { id: "domain", header: "Domain", cell: () => <RaggedTextSkeleton /> },
  { id: "status", header: "Status", cell: () => <RaggedTextSkeleton /> },
  { id: "date", header: "Date", cell: () => <RaggedTextSkeleton /> },
  { id: "alerts", header: "Alerts", cell: () => <RaggedTextSkeleton /> },
  { id: "assignee", header: "Assignee", cell: () => <RaggedTextSkeleton /> },
];

export interface Props {
  description?: string;
  deEmphasizeResolvedIssues?: boolean;

  onShowMonitors?: () => void;
  onCreateMonitor?: () => void;
  onSelectIssue?: (issueId: string) => void;

  rootQueryKey: string;
  getIssues: typeof getAllIssues;
  getAllIssuesValues: typeof getAllIssuesAllValues;
  getIssuesById: typeof getAllIssuesById;
}

function Table({
  description,
  rootQueryKey,
  getIssues,
  getAllIssuesValues,
  getIssuesById,
  deEmphasizeResolvedIssues = false,
  onShowMonitors,
  onCreateMonitor,
  onSelectIssue,
}: Props) {
  const pageSize = 15;

  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const loadingData = useMemo(() => Array(pageSize).fill({}), [pageSize]);

  const issuesListQuery = useQuery({
    queryKey: [
      rootQueryKey,
      "issues",
      "list",
      pagination,
      sorting,
      columnFilters,
      globalFilter,
    ],
    queryFn: () => getIssues(pagination, sorting, columnFilters, globalFilter),
    placeholderData: keepPreviousData,
    select: useCallback(
      (data: {
        issues: MonitoringIssue[];
        total: number;
        totalPages: number;
      }) => ({
        ...data,
        issues: data.issues.map((issue) => ({
          ...issue,
          alerts: [
            ...new Set(
              issue.alerts
                .map((alert) => alert.type)
                .flat()
                .sort(),
            ),
          ].join(", "),
        })),
      }),
      [],
    ),
  });

  const allValuesQuery = useQuery({
    queryKey: [rootQueryKey, "issues", "allValues"],
    queryFn: getAllIssuesValues,
  });

  const table = useReactTable({
    data:
      issuesListQuery.data && allValuesQuery.data
        ? issuesListQuery.data.issues
        : loadingData,
    getRowId: (originalRow) => originalRow.id,
    columns:
      issuesListQuery.data && allValuesQuery.data ? columns : loadingColumns,
    filterFns: {
      dateRange: (
        row: Row<MonitoringIssue>,
        columnId: string,
        filterValue: any,
      ) => {
        const value = row.getValue(columnId) as string;
        const comparator = filterValue?.comparator as string;
        const cutoffDate = filterValue?.cutoffDate as Date;

        if (!comparator) return false;
        if (!cutoffDate) return false;

        try {
          const parsed = parseISO(value);
          if (comparator === "gte") {
            return parsed >= cutoffDate;
          } else if (comparator === "lte") {
            return parsed <= cutoffDate;
          } else {
            return false;
          }
        } catch (e) {
          return false;
        }
      },
    },

    enableRowSelection: true,

    state: {
      rowSelection,
      pagination,
      sorting,
      columnFilters,
    },

    getCoreRowModel: getCoreRowModel(),

    manualPagination: true,
    onPaginationChange: setPagination,

    manualSorting: true,
    onSortingChange: (updaterOrValue) => {
      setSorting(updaterOrValue);
      setPagination((pagination) => ({ ...pagination, pageIndex: 0 }));
    },

    manualFiltering: true,
    onColumnFiltersChange: (updaterOrValue) => {
      setColumnFilters(updaterOrValue);
      setPagination((pagination) => ({ ...pagination, pageIndex: 0 }));
    },

    onRowSelectionChange: setRowSelection,

    columnResizeDirection: "ltr",
    columnResizeMode: "onChange",
  });

  return (
    <>
      <Stack direction="row" sx={{ mb: 2, gap: "1em" }}>
        <GlobalFilter
          value={globalFilter}
          onChange={setGlobalFilter}
          onClear={() => setGlobalFilter("")}
          description={description}
        />
        {isEmpty(rowSelection) ? (
          <>
            <Button
              variant="outlined"
              size="small"
              sx={{
                display: "flex",
                flexShrink: 0,
              }}
              startIcon={<AddIcon />}
              onClick={() => {
                onCreateMonitor?.();
              }}
            >
              Enroll
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{
                display: "flex",
                flexShrink: 0,
              }}
              startIcon={<MenuOpenOutlinedIcon />}
              onClick={() => {
                onShowMonitors?.();
              }}
            >
              See all enrollments
            </Button>
          </>
        ) : (
          <>
            <AssignButton
              ids={Object.keys(rowSelection)}
              onSuccess={() => {
                table.resetRowSelection();
              }}
              invalidateQueryKey={[rootQueryKey, "issues"]}
              listQueryKey={[
                rootQueryKey,
                "issues",
                "list",
                pagination,
                sorting,
                columnFilters,
                globalFilter,
              ]}
            />
            <ExportButton
              ids={Object.keys(rowSelection)}
              getIssuesById={getIssuesById}
            />
          </>
        )}
      </Stack>
      <div className={cn("tableWrap")}>
        <table className={cn("table")}>
          <thead className={cn("left")}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    className={cn("headercol", {
                      left: i === 0,
                      small: i === 0,
                    })}
                    style={{
                      width: i === 0 ? undefined : header.getSize(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5em",
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {i > 0 && (
                          <ColumnFilter
                            key={header.column.id}
                            disabled={!!issuesListQuery.isLoading}
                            active={
                              !!header.column.getIsFiltered() ||
                              !!header.column.getIsSorted()
                            }
                            onReset={() => {
                              header.column.setFilterValue(undefined);
                              header.column.clearSorting();
                            }}
                          >
                            <SimpleSort
                              currentSort={header.column.getIsSorted() || null}
                              onApply={(sort) => {
                                if (!header.column.getCanSort()) return;

                                if (!sort) {
                                  header.column.clearSorting();
                                  return;
                                }

                                if (sort === "desc") {
                                  header.column.toggleSorting(true, true);
                                  return;
                                }

                                if (sort === "asc") {
                                  header.column.toggleSorting(false, true);
                                  return;
                                }
                              }}
                            />
                            {header.column.columnDef.meta?.filterVariant ===
                              "valuePicker" && (
                              <ValuePickerFilter
                                allValues={
                                  allValuesQuery.data &&
                                  header.column.id in allValuesQuery.data
                                    ? allValuesQuery.data[
                                        header.column
                                          .id as keyof typeof allValuesQuery.data
                                      ]
                                    : undefined
                                }
                                stickyValues={
                                  header.column.columnDef.meta
                                    ?.filterStickyValues
                                }
                                selectedValues={
                                  header.column.getIsFiltered()
                                    ? (header.column.getFilterValue() as string[])
                                    : allValuesQuery.data &&
                                        header.column.id in allValuesQuery.data
                                      ? allValuesQuery.data[
                                          header.column
                                            .id as keyof typeof allValuesQuery.data
                                        ]
                                      : undefined
                                }
                                onApply={(
                                  selectedValues: string[] | null | undefined,
                                ) => {
                                  const allValues = sortBy(
                                    uniq(
                                      allValuesQuery.data &&
                                        header.column.id in allValuesQuery.data
                                        ? allValuesQuery.data[
                                            header.column
                                              .id as keyof typeof allValuesQuery.data
                                          ]
                                        : [],
                                    ),
                                  );

                                  const selected = sortBy(
                                    uniq(selectedValues || []),
                                  );

                                  if (isEqual(allValues, selected)) {
                                    header.column.setFilterValue(undefined);
                                  } else {
                                    // we can't use header.column.setFilterValue directly
                                    // because some of the defaults/truthiness assumptions
                                    // lead to wonky ux
                                    setColumnFilters((filters) => {
                                      const updatedFilters: ColumnFiltersState =
                                        [
                                          {
                                            id: header.column.id,
                                            value: selected,
                                          },
                                        ];

                                      for (const filter of filters) {
                                        if (filter.id !== header.column.id) {
                                          updatedFilters.push(filter);
                                        }
                                      }

                                      return updatedFilters;
                                    });
                                  }
                                }}
                              />
                            )}
                            {header.column.columnDef.meta?.filterVariant ===
                              "dateThreshold" && (
                              <DateThresholdFilter
                                cutoffDate={
                                  header.column.getIsFiltered()
                                    ? (header.column.getFilterValue() as any)
                                        ?.cutoffDate
                                    : undefined
                                }
                                comparator={
                                  header.column.getIsFiltered()
                                    ? (header.column.getFilterValue() as any)
                                        ?.comparator
                                    : undefined
                                }
                                onApply={(cutoffDate, comparator) => {
                                  if (!cutoffDate) {
                                    setColumnFilters((oldFilters) =>
                                      oldFilters.filter(
                                        (f) => f.id !== header.column.id,
                                      ),
                                    );
                                  } else {
                                    setColumnFilters((oldFilters) => {
                                      const newFilters = oldFilters.filter(
                                        (f) => f.id !== header.column.id,
                                      );
                                      newFilters.push({
                                        id: header.column.id,
                                        value: { comparator, cutoffDate },
                                      });

                                      return newFilters;
                                    });
                                  }
                                }}
                              />
                            )}
                          </ColumnFilter>
                        )}
                        {i > 0 && i < headerGroup.headers.length - 1 && (
                          <div
                            onDoubleClick={() => header.column.resetSize()}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={cn("resizer", {
                              isResizing: header.column.getIsResizing(),
                            })}
                          />
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={cn("left")}>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={cn({
                  selected: row.getIsSelected(),
                  resolved:
                    deEmphasizeResolvedIssues &&
                    row.getValue("status") === IssueStatus.Resolved,
                })}
                onClick={() => {
                  onSelectIssue?.(row.id);
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className={cn("pagination")}>
          <Pagination
            count={issuesListQuery.data?.totalPages}
            onChange={(_, page) => {
              const pageIndex = Math.max(page - 1, 0);
              table.setPageIndex(pageIndex);
            }}
            size="small"
            showFirstButton
            showLastButton
          />
          <div
            className={cn("rowcounts")}
            style={{ display: "flex", alignItems: "center", gap: "0.5em" }}
          >
            {issuesListQuery.data != null && (
              <>
                {table.getRowCount() > 0 && (
                  <>
                    {pagination.pageIndex * pagination.pageSize + 1} -{" "}
                    {Math.min(
                      (pagination.pageIndex + 1) * pagination.pageSize,
                      issuesListQuery.data.total,
                    )}{" "}
                    of {issuesListQuery.data.total}
                  </>
                )}
                {!isEmpty(rowSelection) &&
                  ` (${Object.keys(rowSelection).length} selected)`}
              </>
            )}
            {issuesListQuery.isFetching &&
              issuesListQuery.isPlaceholderData && (
                <CircularProgress size={12} />
              )}
          </div>
        </div>
      </div>
    </>
  );
}

type SubtableProps = Pick<
  Props,
  "onShowMonitors" | "onCreateMonitor" | "onSelectIssue"
>;

export function AllIssuesTable(props: SubtableProps) {
  return (
    <Table
      description="all issues"
      rootQueryKey="all"
      getIssues={getAllIssues}
      getAllIssuesValues={getAllIssuesAllValues}
      getIssuesById={getAllIssuesById}
      deEmphasizeResolvedIssues
      {...props}
    />
  );
}

export function usePrefetchAllIssuesTableQueries() {
  const client = useQueryClient();

  const pagination = { pageIndex: 0, pageSize: 15 };
  const sorting: SortingState = [];
  const filtering: ColumnFiltersState = [];
  const globalFilter = "";

  client.prefetchQuery({
    queryKey: [
      "all",
      "issues",
      "list",
      pagination,
      sorting,
      filtering,
      globalFilter,
    ],
    queryFn: () => getAllIssues(pagination, sorting, filtering, globalFilter),
  });

  client.prefetchQuery({
    queryKey: ["all", "issues", "allValues"],
    queryFn: getAllIssuesAllValues,
  });
}

export function OpenIssuesTable(props: SubtableProps) {
  return (
    <Table
      description="open issues"
      rootQueryKey="open"
      getIssues={getOpenIssues}
      getAllIssuesValues={getOpenIssuesAllValues}
      getIssuesById={getOpenIssuesById}
      {...props}
    />
  );
}

export function usePrefetchOpenIssuesTableQueries() {
  const client = useQueryClient();

  const pagination = { pageIndex: 0, pageSize: 15 };
  const sorting: SortingState = [];
  const filtering: ColumnFiltersState = [];
  const globalFilter = "";

  client.prefetchQuery({
    queryKey: [
      "open",
      "issues",
      "list",
      pagination,
      sorting,
      filtering,
      globalFilter,
    ],
    queryFn: () => getOpenIssues(pagination, sorting, filtering, globalFilter),
  });

  client.prefetchQuery({
    queryKey: ["open", "issues", "allValues"],
    queryFn: getOpenIssuesAllValues,
  });
}

export function MutedIssuesTable(props: SubtableProps) {
  return (
    <Table
      description="muted issues"
      rootQueryKey="muted"
      getIssues={getMutedIssues}
      getAllIssuesValues={getMutedIssuesAllValues}
      getIssuesById={getMutedIssuesById}
      {...props}
    />
  );
}

export function usePrefetchMutedAllIssuesTableQueries() {
  const client = useQueryClient();

  const pagination = { pageIndex: 0, pageSize: 15 };
  const sorting: SortingState = [];
  const filtering: ColumnFiltersState = [];
  const globalFilter = "";

  client.prefetchQuery({
    queryKey: [
      "muted",
      "issues",
      "list",
      pagination,
      sorting,
      filtering,
      globalFilter,
    ],
    queryFn: () => getMutedIssues(pagination, sorting, filtering, globalFilter),
  });

  client.prefetchQuery({
    queryKey: ["muted", "issues", "allValues"],
    queryFn: getMutedIssuesAllValues,
  });
}

export function ResolvedIssuesTable(props: SubtableProps) {
  return (
    <Table
      description="resolved issues"
      rootQueryKey="resolved"
      getIssues={getResolvedIssues}
      getAllIssuesValues={getResolvedIssuesAllValues}
      getIssuesById={getResolvedIssuesById}
      {...props}
    />
  );
}

export function usePrefetchResolvedIssuesTableQueries() {
  const client = useQueryClient();

  const pagination = { pageIndex: 0, pageSize: 15 };
  const sorting: SortingState = [];
  const filtering: ColumnFiltersState = [];
  const globalFilter = "";

  client.prefetchQuery({
    queryKey: [
      "resolved",
      "issues",
      "list",
      pagination,
      sorting,
      filtering,
      globalFilter,
    ],
    queryFn: () =>
      getResolvedIssues(pagination, sorting, filtering, globalFilter),
  });

  client.prefetchQuery({
    queryKey: ["resolved", "issues", "allValues"],
    queryFn: getResolvedIssuesAllValues,
  });
}
